import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import { TranslateFunction } from 'react-localize-redux';

import { PriceTag, SectionItem } from '../../../../../components/atoms';
import { Currency } from '../../../../../core/api/general/general.types';
import {
  CollectionRestaurantDetail,
  DeliveryRestaurantDetail,
} from '../../../../../core/api/restaurant/restaurant.types';
import { formatNumberToCurrency } from '../../../../../utils/basketUtils';
import * as BasketControl from '../../../../../weborder/BasketControl';
import { SettingEditor } from '../../../components/SettingEditor';
import { checkoutUtils } from '../../../utils';

type Props = {
  basket: BasketControl.Data;
  currency: Currency;
  currentLang: string;
  processingFee: number;
  restaurant: CollectionRestaurantDetail | DeliveryRestaurantDetail;
  toggleExcludeServiceCharge: () => void;
  translate: TranslateFunction;
};

type ServiceChargeData = {
  amount: number;
  isOptional: boolean;
  excludeServiceCharge: boolean;
};

const useStyles = () =>
  makeStyles(() => ({
    priceTag: {
      minHeight: 0,
      whiteSpace: 'nowrap',
    },
  }))();

export function ServiceChargeSetting(props: Props): JSX.Element {
  const {
    basket,
    currency,
    currentLang,
    processingFee,
    restaurant,
    toggleExcludeServiceCharge,
    translate,
  } = props;

  const classes = useStyles();

  const getServiceChargeData = (
    basketData: BasketControl.Data,
    restaurantData: CollectionRestaurantDetail | DeliveryRestaurantDetail,
  ): ServiceChargeData => {
    const {
      DisplayAsOneWithProcessingFee: displayAsOneWithProcessingFee,
      IsOptional: isOptional,
    } = restaurantData.ServiceChargeConfiguration;

    const scAmount = checkoutUtils.computeServiceCharge(
      basketData,
      restaurantData,
    );

    const amount = displayAsOneWithProcessingFee
      ? scAmount + processingFee
      : scAmount;

    return {
      amount,
      isOptional,
      excludeServiceCharge: basket.excludeServiceCharge,
    };
  };

  if (!restaurant.ServiceChargeConfiguration) {
    return null;
  }

  const {
    amount: serviceChargeAmount,
    excludeServiceCharge,
    isOptional: isServiceChargeOptional,
  } = getServiceChargeData(basket, restaurant);

  const handleOnClick = (): void => {
    toggleExcludeServiceCharge();
  };

  const localisedServiceChargeAmount = formatNumberToCurrency(
    serviceChargeAmount,
    currency,
    currentLang,
  );

  const serviceChargeSetting = !excludeServiceCharge && (
    <SectionItem
      dense
      label={translate('Checkout_Service_Charge_Label') as string}
      rightHeader={
        <PriceTag
          className={classes.priceTag}
          data-fd="service-charge-value"
          hasMenuOptions={false}
          localisedPrice={localisedServiceChargeAmount}
          translate={translate}
        />
      }
    />
  );

  return (
    <SettingEditor
      addLabel={translate('Checkout_Add_Service_Charge', {
        amount: localisedServiceChargeAmount,
      })}
      changeLabel={
        isServiceChargeOptional && translate('Checkout_Remove_Service_Charge')
      }
      fdKey="service-charge"
      handleOnAdd={handleOnClick}
      handleOnChange={isServiceChargeOptional && handleOnClick}
      setting={serviceChargeSetting}
    />
  );
}
